import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import BookmarkButton from '../components/BookmarkButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import FramedAvatar from '../components/FramedAvatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { renderWithMentions } from '../utils/mentionParser';
import ChatTextarea from '../components/chat/ChatTextarea';
import {
  Hash,
  Send,
  Pin,
  Bell,
  BellOff,
  Users,
  Calendar,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  Anchor,
  Crown,
  Star,
  Shield,
  User,
  Video,
  Megaphone,
  HelpCircle,
  FolderOpen,
  Search,
  Sun,
  Moon,
  Flag,
  Smile,
  Trophy,
  TrendingUp,
  BookOpen,
  Archive,
  ArrowLeft,
  Cog,
  Reply,
  AtSign,
  MessageCircle,
  Palette,
  ImageIcon,
  Check,
  Lock,
  ScrollText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PushNotificationManager from '../components/PushNotificationManager';
import { useAnimations } from '../context/AnimationContext';
import ReportMessage from '../components/ReportMessage';
import CommunityCharter from '../components/CommunityCharter';
import { useOnlineStatus, OnlineIndicator } from '../components/OnlineStatus';
import { useUnread } from '../context/UnreadContext';

const EMOJI_LIST = ['👍', '❤️', '🔥', '👏', '😂', '🎉', '💡', '✅'];

const DashboardPage = () => {
  const { user, logout, api, canModerate } = useAuth();
  const { currentTheme, toggleTheme } = useTheme();
  const { showMessageSent, showXpGain, triggerNavigation } = useAnimations();
  const { onlineStatus } = useOnlineStatus();
  const { counts: unreadCounts, markChannelRead } = useUnread();
  const navigate = useNavigate();
  
  const [channelGroups, setChannelGroups] = useState([]);
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [membersOpen, setMembersOpen] = useState(true);
  const [openGroups, setOpenGroups] = useState({});
  // Desktop sidebar collapse (persisted in localStorage)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('odys.sidebar.collapsed') === '1'; } catch { return false; }
  });
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('odys.sidebar.collapsed', next ? '1' : '0'); } catch (e) { /* noop */ }
      return next;
    });
  };

  // Mark a specific message as unread (and everything after) for the current user
  const handleMarkMessageUnread = async (message) => {
    try {
      await api.post(`/messages/${message.id}/mark-unread`);
      toast.success('Marqué comme non lu');
      // If user stays in this channel, the badge will reappear on next poll.
      // Force a soft refresh of unread counts immediately by triggering navigation away/back is overkill;
      // the polling hook (10s) will catch it. Optimistic: do nothing visual on the message itself.
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Memoize active messages (filter out archived) — runs every render otherwise
  const activeMessages = useMemo(
    () => messages.filter(m => !m.is_archived),
    [messages]
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [faq, setFaq] = useState([]);
  const [showFaqDialog, setShowFaqDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const [threadReplies, setThreadReplies] = useState([]);
  const [threadReplyMessage, setThreadReplyMessage] = useState('');
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [showThreadEmojiPicker, setShowThreadEmojiPicker] = useState(null);
  const [channelBackground, setChannelBackground] = useState('default');
  const [customChannelBg, setCustomChannelBg] = useState(null);
  const [availableBackgrounds, setAvailableBackgrounds] = useState([]);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [showCharterDialog, setShowCharterDialog] = useState(false);
  const [mobileShowMessages, setMobileShowMessages] = useState(false);
  
  const messagesEndRef = useRef(null);
  const threadEndRef = useRef(null);
  const bgInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle channel selection (for mobile navigation)
  const handleSelectChannel = (channel) => {
    setActiveChannel(channel);
    setMobileShowMessages(true);
    // Mark channel as read
    api.post(`/channels/${channel.id}/read`).catch(() => {});
    markChannelRead(channel.id);
  };

  // Handle back button on mobile
  const handleMobileBack = () => {
    setMobileShowMessages(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeChannel) {
      fetchMessages(activeChannel.id);
      // Optimistic: clear unread for current channel locally
      markChannelRead(activeChannel.id);
    }
  }, [activeChannel, markChannelRead]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchBackgroundPrefs();
  }, []);

  const fetchBackgroundPrefs = async () => {
    try {
      const [bgRes, profileRes] = await Promise.all([
        api.get('/auth/channel-backgrounds'),
        api.get('/auth/profile')
      ]);
      setAvailableBackgrounds(bgRes.data);
      setChannelBackground(profileRes.data.channel_background || 'anchors');
      setCustomChannelBg(profileRes.data.custom_channel_bg);
    } catch (error) {
      console.error('Background prefs error:', error);
    }
  };

  const handleChangeBackground = async (bgId) => {
    try {
      await api.put('/auth/channel-background', { background_id: bgId });
      setChannelBackground(bgId);
      toast.success('🎨 Fond mis à jour !');
      setShowBgPicker(false);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUploadBackground = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/auth/channel-background/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setChannelBackground(response.data.background_id);
      setCustomChannelBg(response.data.preview_url);
      toast.success('🖼️ Fond personnalisé ajouté !');
      setShowBgPicker(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload');
    }
  };

  const getBackgroundStyle = () => {
    if (channelBackground === 'default') return {};
    
    if (channelBackground.startsWith('custom_') && customChannelBg) {
      return { 
        backgroundImage: `url(${customChannelBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    
    const bg = availableBackgrounds.find(b => b.id === channelBackground);
    if (!bg) return {};
    
    if (bg.type === 'gradient') {
      return { background: bg.value };
    }
    
    if (bg.type === 'pattern') {
      const patterns = {
        waves: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 1440 320\'%3E%3Cpath fill=\'%230099ff\' fill-opacity=\'0.05\' d=\'M0,96L48,112C96,128,192,160,288,186.7C384,213,480,235,576,213.3C672,192,768,128,864,128C960,128,1056,192,1152,208C1248,224,1344,192,1392,176L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z\'%3E%3C/path%3E%3C/svg%3E")',
        nautical: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(6,182,212,0.03) 10px, rgba(6,182,212,0.03) 20px)',
        anchors: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'60\' height=\'60\' viewBox=\'0 0 60 60\'%3E%3Ctext x=\'30\' y=\'35\' text-anchor=\'middle\' font-size=\'20\' fill=\'%2306b6d4\' opacity=\'0.08\'%3E⚓%3C/text%3E%3C/svg%3E")',
        stars: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.05) 1px, transparent 1px), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.05) 1px, transparent 1px)',
        compass: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\' viewBox=\'0 0 100 100\'%3E%3Ctext x=\'50\' y=\'55\' text-anchor=\'middle\' font-size=\'30\' fill=\'%2306b6d4\' opacity=\'0.05\'%3E🧭%3C/text%3E%3C/svg%3E")'
      };
      return { backgroundImage: patterns[bg.value] || '' };
    }
    
    return {};
  };

  const fetchData = async () => {
    try {
      const [groupsRes, channelsRes, membersRes, faqRes] = await Promise.all([
        api.get('/channel-groups'),
        api.get('/channels'),
        api.get('/members'),
        api.get('/faq')
      ]);
      
      setChannelGroups(groupsRes.data);
      setChannels(channelsRes.data);
      setMembers(membersRes.data);
      setFaq(faqRes.data);
      
      // Open all groups by default
      const defaultOpen = {};
      groupsRes.data.forEach(g => { defaultOpen[g.id] = true; });
      setOpenGroups(defaultOpen);
      
      if (channelsRes.data.length > 0 && !activeChannel) {
        setActiveChannel(channelsRes.data[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (channelId) => {
    setLoadingMessages(true);
    try {
      const response = await api.get(`/channels/${channelId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault?.();
    if (!newMessage.trim() || !activeChannel) return;

    setSendingMessage(true);
    try {
      const response = await api.post('/messages', {
        content: newMessage.trim(),
        channel_id: activeChannel.id,
        message_type: activeChannel.channel_type === 'video' ? 'video' : 'text'
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
      showMessageSent(); // Animation de confirmation
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingMessage(false);
    }
  };

  const handlePinMessage = async (messageId) => {
    try {
      await api.post(`/messages/${messageId}/pin`);
      toast.success('Message épinglé');
      fetchMessages(activeChannel.id);
    } catch (error) {
      toast.error('Erreur lors de l\'épinglage');
    }
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      const response = await api.post(`/messages/${messageId}/react?emoji=${encodeURIComponent(emoji)}`);
      setMessages(messages.map(m => m.id === messageId ? { ...m, reactions: response.data.reactions } : m));
      setShowEmojiPicker(null);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleReport = async (messageId) => {
    if (!reportReason.trim()) return;
    try {
      await api.post('/reports', { message_id: messageId, reason: reportReason });
      toast.success('Signalement envoyé');
      setShowReportDialog(null);
      setReportReason('');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const response = await api.post('/search/messages', { query: searchQuery });
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Erreur de recherche');
    }
  };

  const handleSubscribe = async (channelId) => {
    try {
      const isSubscribed = user.subscribed_channels?.includes(channelId);
      if (isSubscribed) {
        await api.post(`/channels/${channelId}/unsubscribe`);
        toast.success('Désabonné');
      } else {
        await api.post(`/channels/${channelId}/subscribe`);
        toast.success('Abonné');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Thread functions
  const openThread = async (message) => {
    setActiveThread(message);
    setLoadingThread(true);
    try {
      const response = await api.get(`/messages/${message.id}/replies`);
      setThreadReplies(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des réponses');
    } finally {
      setLoadingThread(false);
    }
  };

  const closeThread = () => {
    setActiveThread(null);
    setThreadReplies([]);
    setThreadReplyMessage('');
  };

  const sendThreadReply = async () => {
    if (!threadReplyMessage.trim() || !activeThread) return;
    setSendingMessage(true);
    try {
      const response = await api.post('/messages', {
        content: threadReplyMessage.trim(),
        channel_id: activeChannel.id,
        parent_id: activeThread.id
      });
      setThreadReplies([...threadReplies, response.data]);
      setThreadReplyMessage('');
      // Update reply count in main messages
      setMessages(messages.map(m => 
        m.id === activeThread.id 
          ? { ...m, reply_count: (m.reply_count || 0) + 1 }
          : m
      ));
      setActiveThread({ ...activeThread, reply_count: (activeThread.reply_count || 0) + 1 });
      showMessageSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle reactions on thread replies
  const handleThreadReplyReaction = async (replyId, emoji) => {
    try {
      const response = await api.post(`/messages/${replyId}/react?emoji=${encodeURIComponent(emoji)}`);
      setThreadReplies(threadReplies.map(r => 
        r.id === replyId ? { ...r, reactions: response.data.reactions } : r
      ));
      setShowThreadEmojiPicker(null);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Mention functions
  const handleMessageChange = async (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Check for @ mentions
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.slice(lastAtIndex + 1);
      const spaceAfterAt = textAfterAt.indexOf(' ');
      if (spaceAfterAt === -1 && textAfterAt.length > 0) {
        // Show mention suggestions
        try {
          const response = await api.get(`/users/mentionable?q=${textAfterAt}`);
          setMentionSuggestions(response.data);
          setShowMentions(response.data.length > 0);
        } catch (error) {
          setShowMentions(false);
        }
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user) => {
    const lastAtIndex = newMessage.lastIndexOf('@');
    const newText = newMessage.slice(0, lastAtIndex) + `@${user.name.split(' ')[0]} `;
    setNewMessage(newText);
    setShowMentions(false);
  };

  const getChannelIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="w-4 h-4 channel-type-video" />;
      case 'announcements': return <Megaphone className="w-4 h-4 channel-type-announcements" />;
      case 'qa': return <HelpCircle className="w-4 h-4 channel-type-qa" />;
      case 'resources': return <FolderOpen className="w-4 h-4 channel-type-resources" />;
      default: return <Hash className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-3 h-3" />;
      case 'major': return <Crown className="w-3 h-3" />;
      case 'ambassadeur': return <Star className="w-3 h-3" />;
      default: return <User className="w-3 h-3" />;
    }
  };

  const getRoleLabel = (role) => {
    const labels = { admin: 'Admin', major: 'Major', ambassadeur: 'Ambassadeur', odys_mentoring: 'Mentoring', odys_start: 'Start' };
    return labels[role] || role;
  };

  const getRoleBadgeClass = (role) => {
    const classes = { admin: 'role-admin', major: 'role-major', ambassadeur: 'role-ambassadeur', odys_mentoring: 'role-odys_mentoring', odys_start: 'role-odys_start' };
    return classes[role] || '';
  };

  const getInitials = (firstName, lastName) => `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

  const getChannelsForGroup = (groupId) => channels.filter(c => c.group_id === groupId);
  const ungroupedChannels = channels.filter(c => !c.group_id);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center gold-glow mx-auto mb-4 animate-pulse">
            <Anchor className="w-7 h-7 text-primary" />
          </div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-full-height md:h-[calc(100vh-3.5rem)] flex overflow-hidden bg-background" data-testid="dashboard">
      <div className="noise-overlay" />

      {/* Left Sidebar */}
      <aside
        className={`
          ${mobileShowMessages ? 'hidden md:flex' : 'flex'}
          ${sidebarCollapsed ? 'sidebar-collapsed' : ''}
          w-full md:w-64 ${sidebarCollapsed ? 'md:!w-16' : ''}
          flex-shrink-0 border-r border-border bg-card/50 backdrop-blur-xl transition-[width] duration-300 overflow-hidden flex-col pb-16
        `}
        data-testid="channels-sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center gold-glow flex-shrink-0">
                <Anchor className="w-5 h-5 text-primary" />
              </div>
              <div className="brand-text min-w-0">
                <h1 className="font-heading font-bold text-lg truncate">L'Équipage <span className="text-primary">Odys</span></h1>
                <p className="text-xs text-muted-foreground truncate">Communauté</p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Desktop sidebar collapse toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebarCollapsed}
                className="hidden md:inline-flex h-8 w-8"
                title={sidebarCollapsed ? 'Agrandir la barre' : 'Replier la barre'}
                data-testid="sidebar-collapse-toggle"
              >
                {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4 -rotate-90" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Channel Groups */}
        <ScrollArea className="flex-1 px-2 py-4">
          {channelGroups.map((group) => {
            const groupChannels = getChannelsForGroup(group.id);
            const groupUnreadTotal = groupChannels.reduce(
              (sum, ch) => sum + (activeChannel?.id !== ch.id ? (unreadCounts[ch.id] || 0) : 0),
              0
            );
            const groupHasUnread = groupUnreadTotal > 0;
            return (
            <Collapsible key={group.id} open={openGroups[group.id]} onOpenChange={(open) => setOpenGroups({ ...openGroups, [group.id]: open })}>
              <CollapsibleTrigger className="flex items-center justify-between w-full px-2 py-1 mb-1 hover:bg-secondary/30 rounded-md">
                <span
                  className={`text-xs uppercase tracking-wider group-label-text ${groupHasUnread ? 'font-bold text-foreground' : 'font-semibold text-muted-foreground'}`}
                >
                  {group.name}
                </span>
                <span className="flex items-center gap-2">
                  {groupHasUnread && (
                    <span
                      className="flex items-center justify-center min-w-[18px] h-4 px-1.5 rounded-full text-[9px] font-bold"
                      style={{ background: 'hsl(var(--gold))', color: '#fff' }}
                      data-testid={`group-unread-${group.id}`}
                      title={`${groupUnreadTotal} message${groupUnreadTotal > 1 ? 's' : ''} non lu${groupUnreadTotal > 1 ? 's' : ''}`}
                    >
                      {groupUnreadTotal > 99 ? '99+' : groupUnreadTotal}
                    </span>
                  )}
                  {openGroups[group.id] ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 mb-3">
                {groupChannels.map((channel) => {
                  const isUnread = unreadCounts[channel.id] > 0 && activeChannel?.id !== channel.id;
                  return (
                    <div
                      key={channel.id}
                      className={`channel-item ${activeChannel?.id === channel.id ? 'active' : ''} ${isUnread ? 'has-unread' : ''}`}
                      onClick={() => handleSelectChannel(channel)}
                      data-testid={`channel-${channel.name}`}
                      title={sidebarCollapsed ? channel.name : undefined}
                    >
                      {getChannelIcon(channel.channel_type)}
                      <span className="truncate flex-1">{channel.name}</span>
                      {isUnread && (
                        <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold" data-testid={`unread-badge-${channel.id}`}>
                          {unreadCounts[channel.id] > 99 ? '99+' : unreadCounts[channel.id]}
                        </span>
                      )}
                      {user.subscribed_channels?.includes(channel.id) && <Bell className="w-3 h-3 text-primary" />}
                      <ChevronRight className="w-4 h-4 text-muted-foreground md:hidden" />
                    </div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
            );
          })}

          {ungroupedChannels.length > 0 && (
            <div className="mb-3">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 group-label-text">Autres</span>
              <div className="space-y-1 mt-1">
                {ungroupedChannels.map((channel) => {
                  const isUnread = unreadCounts[channel.id] > 0 && activeChannel?.id !== channel.id;
                  return (
                    <div
                      key={channel.id}
                      className={`channel-item ${activeChannel?.id === channel.id ? 'active' : ''} ${isUnread ? 'has-unread' : ''}`}
                      onClick={() => handleSelectChannel(channel)}
                      data-testid={`channel-${channel.name}`}
                      title={sidebarCollapsed ? channel.name : undefined}
                    >
                      {getChannelIcon(channel.channel_type)}
                      <span className="truncate flex-1">{channel.name}</span>
                      {isUnread && (
                        <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold" data-testid={`unread-badge-${channel.id}`}>
                          {unreadCounts[channel.id] > 99 ? '99+' : unreadCounts[channel.id]}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-muted-foreground md:hidden" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Separator className="my-4" />

          {/* Navigation */}
          <div className="space-y-1">
            <div className="channel-item bg-primary/10 border border-primary/20" onClick={() => navigate('/')} data-testid="nav-home">
              <Anchor className="w-4 h-4 text-primary" />
              <span className="text-primary font-medium">Dashboard</span>
            </div>
            <div className="channel-item" onClick={() => setShowSearch(true)} data-testid="nav-search">
              <Search className="w-4 h-4 text-muted-foreground" />
              <span>Recherche</span>
            </div>
            <div className="channel-item" onClick={() => navigate('/calendar')} data-testid="nav-calendar">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span>Calendrier</span>
            </div>
            <div className="channel-item" onClick={() => navigate('/dm')} data-testid="nav-messages">
              <MessageSquare className="w-4 h-4 text-muted-foreground" />
              <span>Messages privés</span>
            </div>
            <div className="channel-item" onClick={() => navigate('/leaderboard')} data-testid="nav-leaderboard">
              <Trophy className="w-4 h-4 text-muted-foreground" />
              <span>Classement</span>
            </div>
            <div className="channel-item" onClick={() => setShowFaqDialog(true)} data-testid="nav-faq">
              <BookOpen className="w-4 h-4 text-muted-foreground" />
              <span>FAQ</span>
            </div>
            <div className="channel-item" onClick={() => setShowCharterDialog(true)} data-testid="nav-charter">
              <ScrollText className="w-4 h-4 text-muted-foreground" />
              <span>Charte</span>
            </div>
            <div className="channel-item" onClick={() => setShowSettingsDialog(true)} data-testid="nav-settings">
              <Cog className="w-4 h-4 text-muted-foreground" />
              <span>Paramètres</span>
            </div>
            {(user.role === 'admin' || user.role === 'major') && (
              <div className="channel-item" onClick={() => navigate('/admin')} data-testid="nav-admin">
                <Settings className="w-4 h-4 text-muted-foreground" />
                <span>Administration</span>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* User Profile */}
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3">
            <FramedAvatar
              src={user.avatar_url}
              fallback={getInitials(user.first_name, user.last_name)}
              frame={user.profile_frame}
              role={user.role}
              seniority={user.seniority}
              size="chat"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.first_name} {user.last_name}</p>
              <div className="flex items-center gap-2">
                <Badge className={`text-xs px-1.5 py-0 ${getRoleBadgeClass(user.role)}`}>{getRoleLabel(user.role)}</Badge>
                <span className="level-badge">Niv.{user.level || 1}</span>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive" onClick={logout} data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 flex flex-col min-w-0 ${!mobileShowMessages ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <header className="h-14 border-b border-border flex items-center justify-between px-4 bg-card/30 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            {/* Mobile Back Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden" 
              onClick={handleMobileBack}
              data-testid="mobile-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            {activeChannel && (
              <div className="flex items-center gap-2">
                {getChannelIcon(activeChannel.channel_type)}
                <h2 className="font-heading font-semibold text-lg">{activeChannel.name}</h2>
                {activeChannel.is_read_only && (
                  <Badge variant="outline" className="text-xs text-yellow-500 border-yellow-500/30">
                    <Lock className="w-3 h-3 mr-1" />
                    Lecture seule
                  </Badge>
                )}
                <span className="text-sm text-muted-foreground hidden sm:inline">{activeChannel.description}</span>
                {activeChannel.tags?.length > 0 && (
                  <div className="hidden md:flex gap-1">
                    {activeChannel.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="tag">#{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {activeChannel?.rules && (
              <Button variant="ghost" size="icon" onClick={() => setShowRulesDialog(true)} data-testid="rules-button">
                <BookOpen className="w-5 h-5" />
              </Button>
            )}
            {activeChannel && (
              <Button variant="ghost" size="icon" onClick={() => handleSubscribe(activeChannel.id)} className={user.subscribed_channels?.includes(activeChannel.id) ? 'text-primary' : ''} data-testid="subscribe-button">
                {user.subscribed_channels?.includes(activeChannel.id) ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              </Button>
            )}
            {/* Background picker button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => setShowBgPicker(!showBgPicker)} className="hover:text-cyan-500" data-testid="bg-picker-btn">
                    <Palette className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Personnaliser le fond</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="ghost" size="icon" onClick={() => setMembersOpen(!membersOpen)} className="hidden md:flex" data-testid="toggle-members">
              <Users className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Messages Area */}
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 flex flex-col min-w-0">
            <ScrollArea className="flex-1 p-4 relative" style={getBackgroundStyle()}>
              {/* Background picker dropdown - Premium */}
              {showBgPicker && (
                <div className="absolute top-2 right-2 z-50 bg-card/95 backdrop-blur-xl border border-primary/20 rounded-2xl p-5 shadow-2xl w-80 animate-scale-in">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                        <Palette className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm">Personnaliser le fond</h4>
                        <p className="text-xs text-muted-foreground">Choisis ton ambiance</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-destructive/10 hover:text-destructive" onClick={() => setShowBgPicker(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Preset backgrounds grid */}
                  <div className="space-y-3 mb-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Thèmes prédéfinis</p>
                    <div className="grid grid-cols-5 gap-2">
                      {availableBackgrounds.map(bg => {
                        const previewStyle = bg.type === 'gradient' 
                          ? { background: bg.value } 
                          : bg.type === 'pattern' 
                            ? { background: 'linear-gradient(135deg, #0c4a6e 0%, #164e63 100%)' }
                            : { background: 'hsl(var(--background))' };
                        
                        const patternOverlay = bg.type === 'pattern' ? {
                          waves: '🌊',
                          nautical: '🗺️',
                          anchors: '⚓',
                          stars: '✨',
                          compass: '🧭'
                        }[bg.value] : null;
                        
                        return (
                          <button
                            key={bg.id}
                            onClick={() => handleChangeBackground(bg.id)}
                            className={`relative w-12 h-12 rounded-xl border-2 transition-all duration-200 hover:scale-110 hover:shadow-lg group overflow-hidden ${
                              channelBackground === bg.id 
                                ? 'border-primary ring-2 ring-primary/40 shadow-primary/20 shadow-lg' 
                                : 'border-border/50 hover:border-primary/50'
                            }`}
                            style={previewStyle}
                            title={bg.name}
                          >
                            {patternOverlay && (
                              <span className="absolute inset-0 flex items-center justify-center text-lg opacity-60">
                                {patternOverlay}
                              </span>
                            )}
                            {channelBackground === bg.id && (
                              <div className="absolute inset-0 bg-primary/30 flex items-center justify-center">
                                <Check className="w-5 h-5 text-white drop-shadow-lg" />
                              </div>
                            )}
                            <div className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <span className="text-[8px] text-white font-medium truncate block text-center px-1">{bg.name}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Custom upload section */}
                  <div className="border-t border-border/50 pt-4">
                    <input
                      ref={bgInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleUploadBackground}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full border-dashed border-2 hover:border-primary hover:bg-primary/5 transition-all"
                      onClick={() => bgInputRef.current?.click()}
                    >
                      <ImageIcon className="w-4 h-4 mr-2 text-primary" />
                      <span>Importer une image</span>
                    </Button>
                    <p className="text-[10px] text-muted-foreground text-center mt-2">PNG, JPG ou WebP (max 5 Mo)</p>
                  </div>
                </div>
              )}

              {/* Pinned Message */}
              {messages.find(m => m.is_pinned) && (
                <div className="mb-4 p-3 bg-primary/10 backdrop-blur-sm border border-primary/20 rounded-lg animate-fade-in">
                  <div className="flex items-center gap-2 text-sm text-primary mb-1">
                    <Pin className="w-4 h-4" />
                    <span className="font-medium">Message épinglé</span>
                  </div>
                  <p className="text-sm">{messages.find(m => m.is_pinned)?.content}</p>
                </div>
              )}

              {/* Loading Messages */}
              {loadingMessages && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-muted-foreground">Chargement des messages...</p>
                </div>
              )}

              {/* Messages */}
              {!loadingMessages && (
                <div className="space-y-4">
                  {activeMessages.map((message, index) => (
                  <div key={message.id} className={`flex gap-3 group animate-fade-in ${message.author_id === user.id ? 'flex-row-reverse' : ''}`} style={{ animationDelay: `${index * 0.02}s` }} data-testid={`message-${message.id}`}>
                    <FramedAvatar
                      src={message.author_avatar}
                      fallback={message.author_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      frame={message.author_frame}
                      role={message.author_role}
                      size="chat"
                      className="flex-shrink-0 shadow-md hover:scale-105 transition-transform"
                    />
                    <div className={`flex-1 max-w-[70%] ${message.author_id === user.id ? 'text-right' : ''}`}>
                      <div className={`chat-meta flex items-center gap-2 mb-1 ${message.author_id === user.id ? 'justify-end' : ''}`}>
                        <span className="text-sm font-medium">{message.author_name}</span>
                        <span className="level-badge">Niv.{message.author_level || 1}</span>
                        <span className="text-xs opacity-70">{new Date(message.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div className={`${message.author_id === user.id ? 'message-bubble-own' : 'message-bubble'} inline-block relative backdrop-blur-sm`}>
                        {message.message_type === 'video' && message.media_url && (
                          <video src={message.media_url} controls className="max-w-full rounded mb-2" />
                        )}
                        <p className="text-sm break-words whitespace-pre-wrap text-left">{renderWithMentions(message.content, user?.first_name ? `${user.first_name}_${user.last_name || ''}`.toLowerCase() : null)}</p>
                        
                        {/* Reactions */}
                        {message.reactions && Object.keys(message.reactions).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {Object.entries(message.reactions).map(([emoji, users]) => (
                              <button key={emoji} onClick={() => handleReaction(message.id, emoji)} className={`px-2 py-0.5 rounded-full text-xs bg-secondary/50 hover:bg-secondary hover:scale-110 transition-all ${users.includes(user.id) ? 'ring-1 ring-primary' : ''}`}>
                                {emoji} {users.length}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Tags */}
                        {message.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {message.tags.map(tag => <span key={tag} className="tag text-xs">#{tag}</span>)}
                          </div>
                        )}

                        {/* Thread replies indicator */}
                        {message.reply_count > 0 && (
                          <button
                            onClick={() => openThread(message)}
                            className="flex items-center gap-1 mt-2 text-xs text-primary hover:underline"
                          >
                            <MessageCircle className="w-3 h-3" />
                            {message.reply_count} réponse{message.reply_count > 1 ? 's' : ''}
                          </button>
                        )}
                      </div>

                      {/* Actions */}
                      <div className={`flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${message.author_id === user.id ? 'justify-end' : ''}`}>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openThread(message)}>
                                <Reply className="w-3 h-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Répondre</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowEmojiPicker(showEmojiPicker === message.id ? null : message.id)}>
                                <Smile className="w-3 h-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Réagir</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        
                        {canModerate && !message.is_pinned && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handlePinMessage(message.id)}>
                            <Pin className="w-3 h-3" />
                          </Button>
                        )}

                        <BookmarkButton
                          itemType="message"
                          itemId={message.id}
                          itemTitle={`Message de ${message.author_name}`}
                          itemPreview={message.content?.slice(0, 200)}
                          itemUrl="/"
                        />

                        {message.author_id !== user.id && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => handleMarkMessageUnread(message)}
                                  data-testid={`mark-unread-${message.id}`}
                                >
                                  <BellOff className="w-3 h-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Marquer comme non lu</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}

                        {message.author_id !== user.id && (
                          <ReportMessage 
                            messageId={message.id}
                            messageContent={message.content}
                            authorName={message.author_name}
                          />
                        )}
                      </div>

                      {/* Emoji Picker */}
                      {showEmojiPicker === message.id && (
                        <div className={`flex gap-1 p-2 bg-card border border-border rounded-lg mt-1 ${message.author_id === user.id ? 'justify-end' : ''}`}>
                          {EMOJI_LIST.map(emoji => (
                            <button key={emoji} onClick={() => handleReaction(message.id, emoji)} className="hover:scale-125 transition-transform text-lg">
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              )}
            </ScrollArea>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 pb-24 md:pb-4 border-t border-border bg-card/30 relative">
              {/* Read-only notice */}
              {activeChannel?.is_read_only && !['admin', 'major'].includes(user.role) ? (
                <div className="flex items-center justify-center gap-2 py-3 text-muted-foreground">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">Ce canal est en lecture seule</span>
                </div>
              ) : (
                <>
                  {/* Mention suggestions */}
                  {showMentions && mentionSuggestions.length > 0 && (
                    <div className="absolute bottom-full left-4 right-4 mb-2 bg-card border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {mentionSuggestions.map(u => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => insertMention(u)}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary/50 text-left"
                        >
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={u.avatar_url} />
                            <AvatarFallback className="text-xs">{u.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{u.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 relative">
                      <AtSign className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground pointer-events-none" />
                      <ChatTextarea
                        value={newMessage}
                        onChange={handleMessageChange}
                        onSubmit={handleSendMessage}
                        placeholder={`Message #${activeChannel?.name || ''} (@ pour mentionner)`}
                        className="pl-10"
                        disabled={sendingMessage}
                        testId="message-input"
                      />
                    </div>
                    <Button type="submit" disabled={sendingMessage || !newMessage.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow" data-testid="send-message-button">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>

          {/* Thread Panel */}
          {activeThread && (
            <div className="w-80 border-l border-border bg-card/50 flex flex-col" data-testid="thread-panel">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-heading font-semibold flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-primary" />
                  Discussion
                </h3>
                <Button variant="ghost" size="icon" onClick={closeThread}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Original message */}
              <div className="p-4 border-b border-border bg-secondary/30">
                <div className="flex gap-2">
                  <FramedAvatar
                    src={activeThread.author_avatar}
                    fallback={activeThread.author_name?.split(' ').map(n => n[0]).join('')}
                    frame={activeThread.author_frame}
                    role={activeThread.author_role}
                    size="md"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{activeThread.author_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(activeThread.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{activeThread.content}</p>
                  </div>
                </div>
              </div>

              {/* Replies */}
              <ScrollArea className="flex-1">
                <div className="p-4 space-y-4">
                  {loadingThread ? (
                    <div className="text-center text-muted-foreground text-sm py-4">Chargement...</div>
                  ) : threadReplies.length === 0 ? (
                    <div className="text-center text-muted-foreground text-sm py-4">Aucune réponse. Sois le premier !</div>
                  ) : (
                    threadReplies.map(reply => (
                      <div key={reply.id} className="flex gap-2 group">
                        <FramedAvatar
                          src={reply.author_avatar}
                          fallback={reply.author_name?.split(' ').map(n => n[0]).join('')}
                          frame={reply.author_frame}
                          role={reply.author_role}
                          size="sm"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{reply.author_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(reply.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className="text-sm mt-1 text-muted-foreground">{reply.content}</p>
                          
                          {/* Reactions on replies */}
                          {reply.reactions && Object.keys(reply.reactions).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Object.entries(reply.reactions).map(([emoji, users]) => (
                                <button 
                                  key={emoji} 
                                  onClick={() => handleThreadReplyReaction(reply.id, emoji)} 
                                  className={`px-1.5 py-0.5 rounded-full text-xs bg-secondary/50 hover:bg-secondary ${users.includes(user.id) ? 'ring-1 ring-primary' : ''}`}
                                >
                                  {emoji} {users.length}
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Emoji picker for replies */}
                          <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => setShowThreadEmojiPicker(showThreadEmojiPicker === reply.id ? null : reply.id)}
                              className="p-1 hover:bg-secondary/50 rounded"
                            >
                              <Smile className="w-3 h-3 text-muted-foreground" />
                            </button>
                          </div>
                          
                          {showThreadEmojiPicker === reply.id && (
                            <div className="flex gap-1 p-2 bg-card border border-border rounded-lg mt-1">
                              {EMOJI_LIST.map(emoji => (
                                <button key={emoji} onClick={() => handleThreadReplyReaction(reply.id, emoji)} className="hover:scale-125 transition-transform text-sm">
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={threadEndRef} />
                </div>
              </ScrollArea>

              {/* Reply input */}
              <div className="p-3 border-t border-border">
                <div className="flex gap-2">
                  <Input 
                    value={threadReplyMessage}
                    onChange={(e) => setThreadReplyMessage(e.target.value)}
                    placeholder="Répondre..."
                    className="flex-1 bg-secondary/50 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendThreadReply())}
                    disabled={sendingMessage}
                  />
                  <Button 
                    size="sm" 
                    onClick={sendThreadReply} 
                    disabled={sendingMessage || !threadReplyMessage.trim()}
                    className="bg-primary"
                  >
                    <Send className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Members Sidebar */}
          {membersOpen && (
            <aside className="w-60 border-l border-border bg-card/30 backdrop-blur-sm hidden md:block" data-testid="members-sidebar">
              <div className="p-4 border-b border-border">
                <h3 className="font-heading font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Membres ({members.length})
                </h3>
              </div>
              <ScrollArea className="h-[calc(100%-57px)]">
                <div className="p-2 space-y-1">
                  {['admin', 'major', 'ambassadeur', 'odys_mentoring', 'odys_start'].map(role => {
                    const roleMembers = members.filter(m => m.role === role);
                    if (roleMembers.length === 0) return null;
                    
                    return (
                      <div key={role} className="mb-4">
                        <div className="flex items-center gap-2 px-2 py-1">
                          {getRoleIcon(role)}
                          <span className="text-xs font-semibold text-muted-foreground uppercase">{getRoleLabel(role)} — {roleMembers.length}</span>
                        </div>
                        {roleMembers.map(member => (
                          <div key={member.id} className="member-item cursor-pointer" onClick={() => navigate(`/dm/${member.id}`)} data-testid={`member-${member.id}`}>
                            <div className="relative">
                              <FramedAvatar
                                src={member.avatar_url}
                                fallback={getInitials(member.first_name, member.last_name)}
                                frame={member.profile_frame}
                                role={member.role}
                                seniority={member.seniority}
                                size="md"
                              />
                              <span className="absolute -bottom-0.5 -right-0.5">
                                <OnlineIndicator userId={member.id} onlineStatus={onlineStatus} size="xs" />
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm truncate block">{member.first_name} {member.last_name}</span>
                              <span className="text-xs text-muted-foreground">Niv.{member.level || 1} • {member.points || 0} pts</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </aside>
          )}
        </div>
      </main>

      {/* Search Dialog */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Recherche avancée</DialogTitle>
            <DialogDescription>Recherchez dans tous les messages</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher..." className="flex-1" onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
              <Button onClick={handleSearch}>Rechercher</Button>
            </div>
            <ScrollArea className="h-[400px]">
              {searchResults.map(msg => (
                <div key={msg.id} className="p-3 bg-secondary/30 rounded-lg mb-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{msg.author_name}</span>
                    <span className="text-xs text-muted-foreground">{new Date(msg.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                  <p className="text-sm">{renderWithMentions(msg.content)}</p>
                </div>
              ))}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rules Dialog */}
      <Dialog open={showRulesDialog} onOpenChange={setShowRulesDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Règles du channel</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">{activeChannel?.rules}</p>
        </DialogContent>
      </Dialog>

      {/* FAQ Dialog */}
      <Dialog open={showFaqDialog} onOpenChange={setShowFaqDialog}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Questions fréquentes</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            {faq.map(item => (
              <div key={item.id} className="p-4 bg-secondary/30 rounded-lg mb-3">
                <h4 className="font-semibold mb-2">{item.question}</h4>
                <p className="text-sm text-muted-foreground">{item.answer}</p>
                <Badge className="mt-2 text-xs">{item.category}</Badge>
              </div>
            ))}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={!!showReportDialog} onOpenChange={() => setShowReportDialog(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Signaler ce message</DialogTitle>
            <DialogDescription>Décrivez la raison du signalement</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Raison du signalement..." />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowReportDialog(null)}>Annuler</Button>
              <Button variant="destructive" onClick={() => handleReport(showReportDialog)}>Signaler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Paramètres</DialogTitle>
            <DialogDescription>Gérez vos préférences</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <PushNotificationManager />
          </div>
        </DialogContent>
      </Dialog>

      {/* Charter Dialog */}
      <Dialog open={showCharterDialog} onOpenChange={setShowCharterDialog}>
        <DialogContent className="bg-card border-border w-[min(960px,94vw)] max-w-none max-h-[86vh] overflow-y-auto">
          <CommunityCharter inline={true} showButton={false} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
